import { useEffect, useState, type ReactNode } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import * as Tooltip from '@radix-ui/react-tooltip'
import {
  BarChart3, Box, CalendarCheck, CalendarDays, ExternalLink, FolderKanban, Home,
  Lightbulb, LineChart, LogOut, PanelLeftClose, PanelLeftOpen, Presentation, Settings, ShieldCheck,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useApi } from '../hooks/useApi'
import { roleChips, type RoleChipKind } from '../lib/roles'
import { ROLE_LABELS } from '../types'
import type { AppRole } from '../types'
import TrackitLogo, { TrackitMark } from './TrackitLogo'

const IS_DEV = import.meta.env.DEV || import.meta.env.VITE_PUBLIC_API_URL?.includes('-dev.')

// Whether the sidebar is folded to its icons. Remembered per browser: it is a
// preference about screen space, not about the account.
const SIDEBAR_KEY = 'trackit.sidebar'

export function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'collapsed'
  } catch {
    return false
  }
}

function writeSidebarCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? 'collapsed' : 'expanded')
  } catch {
    // Private mode or blocked storage: the choice lasts for this visit only.
  }
}

interface NavItem {
  to: string
  icon: typeof Home
  label: string
  end?: boolean
  /** Other paths this entry stands for, so it reads as "you are here" on
   *  every page it groups: Reports covers the scorecard and BAU pages. */
  also?: string[]
}

/** The nav as sections, each with the heading shown above it when the sidebar
 *  is open. A section with no heading is separated by its divider alone. */
const SECTIONS: { heading?: { icon: typeof Home; label: string }; items: NavItem[] }[] = [
  {
    items: [
      { to: '/', icon: Home, label: 'Dashboard', end: true },
      { to: '/projects', icon: FolderKanban, label: 'Projects' },
      { to: '/products', icon: Box, label: 'Products' },
    ],
  },
  {
    heading: { icon: LineChart, label: 'Reporting' },
    items: [
      // Delivery Performance, Scorecard and Operational BAU are tabs of one
      // Reports page (components/ReportsTabs), so they take one line here.
      { to: '/reports', icon: BarChart3, label: 'Reports', also: ['/scorecard', '/bau', '/survey-results'] },
      { to: '/ddi', icon: Presentation, label: 'DDI Portfolio' },
      { to: '/month-end', icon: CalendarCheck, label: 'End of Month Review' },
    ],
  },
  {
    items: [{ to: '/settings', icon: Settings, label: 'Settings' }],
  },
]

// Icons are grey until the page is the one you are on, so the blue reads as
// "you are here" rather than as decoration.
const navClass = (collapsed: boolean) => ({ isActive }: { isActive: boolean }) =>
  `sidebar-item group flex items-center rounded-xl text-sm font-medium transition-colors ${
    collapsed ? 'justify-center w-11 h-11 mx-auto' : 'gap-3 px-3 py-2.5'
  } ${isActive ? 'active text-[#005EB8] font-semibold' : 'text-slate-600 hover:text-[#005EB8]'}`

const navIconClass = (isActive: boolean) =>
  `w-5 h-5 flex-shrink-0 transition-colors ${isActive ? 'text-[#005EB8]' : 'text-slate-400 group-hover:text-[#005EB8]'}`

// The chips under the logo, coloured by the kind roleChips gives them.
const CHIP_STYLES: Record<RoleChipKind, string> = {
  platform: 'bg-[#005EB8]/10 text-[#005EB8] border-[#005EB8]/20',
  reporting: 'bg-[#00ACED]/10 text-[#0077A8] border-[#00ACED]/25',
  user: 'bg-slate-100 text-slate-500 border-slate-200',
}

// Same icons as the Settings tabs. Only the highest role gets a badge on the
// avatar, and a plain User gets none rather than a meaningless one.
const ROLE_BADGES: { role: AppRole; icon: typeof ShieldCheck }[] = [
  { role: 'PlatformAdmin', icon: ShieldCheck },
  { role: 'StatusReportingAdmin', icon: CalendarDays },
]

function Avatar({ name, roles }: { name: string; roles: AppRole[] }) {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const badge = ROLE_BADGES.find(({ role }) => roles.includes(role))
  const BadgeIcon = badge?.icon
  return (
    <div className="relative flex-shrink-0">
      <div className="w-9 h-9 rounded-full bg-[#005EB8] flex items-center justify-center ring-2 ring-white shadow-sm">
        <span className="text-white text-xs font-bold">{initials}</span>
      </div>
      {BadgeIcon && badge && (
        <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center">
          <BadgeIcon className="w-2.5 h-2.5 text-[#005EB8]" />
          <span className="sr-only">{ROLE_LABELS[badge.role]}</span>
        </span>
      )}
    </div>
  )
}

/** A label that only shows as a tooltip while the sidebar is folded. */
function Labelled({ label, collapsed, children }: { label: ReactNode; collapsed: boolean; children: ReactNode }) {
  if (!collapsed) return <>{children}</>
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="right" sideOffset={10}
                         className="z-[70] rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-xl">
          {label}
          <Tooltip.Arrow className="fill-slate-900" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

export default function Layout() {
  const { user, logout } = useAuth()
  const api = useApi()
  const [requestAccessUrl, setRequestAccessUrl] = useState<string | undefined>()
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed)

  useEffect(() => {
    let active = true
    api.getSettings()
      .then(settings => { if (active) setRequestAccessUrl(settings.requestAccessUrl) })
      .catch(() => {})
    return () => { active = false }
  }, [api])

  const toggle = () => {
    const next = !collapsed
    setCollapsed(next)
    writeSidebarCollapsed(next)
  }

  const chips = user ? roleChips(user.grants) : []
  const item = navClass(collapsed)
  const { pathname } = useLocation()

  const link = ({ to, icon: Icon, label, end, also }: NavItem) => {
    const alsoHere = !!also?.some(path => pathname === path || pathname.startsWith(`${path}/`))
    return (
      <Labelled key={to} label={label} collapsed={collapsed}>
        <NavLink to={to} end={end} className={({ isActive }) => item({ isActive: isActive || alsoHere })}
                 aria-label={collapsed ? label : undefined}>
          {({ isActive }) => (
            <>
              <Icon className={navIconClass(isActive || alsoHere)} />
              {!collapsed && label}
            </>
          )}
        </NavLink>
      </Labelled>
    )
  }

  const toggleButton = (
    <Labelled label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} collapsed={collapsed}>
      <button type="button" onClick={toggle} aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-[#005EB8] hover:bg-slate-100 transition-colors flex-shrink-0">
        {collapsed ? <PanelLeftOpen className="w-[18px] h-[18px]" /> : <PanelLeftClose className="w-[18px] h-[18px]" />}
      </button>
    </Labelled>
  )

  return (
    <Tooltip.Provider delayDuration={150} skipDelayDuration={100}>
      <div className="screen-container">
        <div className="flex app-body">
          <aside data-collapsed={collapsed || undefined}
                 className={`bg-white/80 backdrop-blur-sm border-r border-slate-200 flex flex-col relative z-10 transition-[width] duration-200 flex-shrink-0 ${
                   collapsed ? 'w-16 px-2 py-4' : 'w-64 p-4'}`}>
            <div className={`mb-5 pb-4 border-b border-slate-200 ${collapsed ? 'flex flex-col items-center gap-2' : ''}`}>
              {collapsed ? (
                <>
                  <Labelled collapsed label={<>TrackIT{IS_DEV && ' · DEV'}</>}>
                    <div><TrackitMark className="w-10 h-10" /></div>
                  </Labelled>
                  {toggleButton}
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <TrackitLogo className="h-8" />
                    {toggleButton}
                  </div>
                  {(chips.length > 0 || IS_DEV) && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {chips.map(chip => (
                        <span key={chip.label}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${CHIP_STYLES[chip.kind]}`}>
                          {chip.label}
                        </span>
                      ))}
                      {IS_DEV && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-600 border border-red-200">DEV</span>
                      )}
                    </div>
                  )}
                  {requestAccessUrl && (
                    <a href={requestAccessUrl} target="_blank" rel="noopener noreferrer"
                       className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-[#005EB8] transition-colors">
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      Request access
                    </a>
                  )}
                </>
              )}
            </div>

            <nav className="flex-1">
              {SECTIONS.map((section, i) => (
                <div key={i} className={i === 0 ? '' : 'mt-4 pt-4 border-t border-slate-200'}>
                  {section.heading && !collapsed && (
                    <div className="flex items-center gap-1.5 px-3 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <section.heading.icon className="w-3.5 h-3.5" />
                      {section.heading.label}
                    </div>
                  )}
                  <div className="space-y-1">{section.items.map(link)}</div>
                </div>
              ))}
            </nav>

            {/* Subtle "Request a feature" CTA. Sits above the user divider
                so it reads as a meta-action (help-desk vibe), not a nav item.
                Routes to an Asana form embed. Muted slate tones to avoid
                competing with the primary Danone cyan used by nav items. */}
            <Labelled label="Request a feature" collapsed={collapsed}>
              <NavLink
                to="/feature-request"
                aria-label={collapsed ? 'Request a feature' : undefined}
                className={({ isActive }) =>
                  // Folded, it is one of the icons; open, it is the quiet
                  // bordered CTA that sits apart from the nav.
                  collapsed
                    ? `sidebar-item group mb-3 flex items-center justify-center w-11 h-11 mx-auto rounded-xl transition-colors ${isActive ? 'active' : ''}`
                    : `mb-3 group flex items-center gap-2.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${isActive
                        ? 'border-slate-300 bg-slate-100 text-slate-700'
                        : 'border-slate-200/70 bg-slate-50/60 text-slate-500 hover:border-slate-300 hover:bg-slate-100 hover:text-slate-700'}`
                }
              >
                {({ isActive }) => (
                  <>
                    <Lightbulb className={collapsed
                      ? navIconClass(isActive)
                      : 'w-3.5 h-3.5 flex-shrink-0 text-slate-400 transition-colors group-hover:text-slate-600'} />
                    {!collapsed && <span className="truncate">Request a feature</span>}
                  </>
                )}
              </NavLink>
            </Labelled>

            {/* Current user + logout */}
            <div className="pt-4 border-t border-slate-200">
              {collapsed ? (
                <div className="flex flex-col items-center gap-3">
                  {user && (
                    <Labelled collapsed label={<><div>{user.name}</div><div className="text-slate-300 font-normal">{user.email}</div></>}>
                      <div><Avatar name={user.name} roles={user.roles} /></div>
                    </Labelled>
                  )}
                  <Labelled collapsed label="Sign out">
                    <button onClick={logout} aria-label="Sign out"
                            className="text-slate-400 hover:text-red-500 transition-colors">
                      <LogOut className="w-4 h-4" />
                    </button>
                  </Labelled>
                </div>
              ) : (
                <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
                  {user && <Avatar name={user.name} roles={user.roles} />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                  </div>
                  <button onClick={logout} title="Sign out"
                    className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0">
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </aside>

          <main className="flex-1 min-w-0 p-8 relative z-10 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </Tooltip.Provider>
  )
}
